import moment from "moment";
import { Formater, TypeRecords } from "src/framework/commons";
import { InvalidException } from "src/framework/exceptions/Error";
import api, { IDataRequest, IDataResponse, STATUS_CODE } from "src/framework/providers/api";
import { AProvider } from "src/framework/providers/AProvider";
import { userInfo$ } from "src/framework/store";
import { ITermoAceite, ITermosPagamentos } from "src/providers/cadastros/TermosAceiteProvider/types";
import { CartaoCreditoParcelaType, Clientes, CupomDescontoEfetivar, CupomDescontoValidar, FinanciamentoSimulacao, FormaPagamentoType, IParcelasDisponiveis, PedidoPagamento, Simulacao } from "src/types";

export interface IGetParcelas {
    valor: number,
    codigo_venda: string;
    entrada: boolean;
    bandeira: string;
}


interface ConsultarCupomDesconto {
    codigo: string;
    codigo_venda: string;
    uf: string;
    potencia: number;
    valor_original: number;
    valor_servico: number;
    valor_desconto: number;
    valor_pendente: number;
    cpf_cnpj: string;
}

interface PedidoCupomDesconto {
    codigo: string;
    valor_desconto: number;
}

class PagamentosProvider extends AProvider {
    constructor() {
        super("/pagamentos");
    }

    async getPagamentos(usuarioId: number): Promise<IDataResponse> {
        try {
            const filter = "usuario_id=" + usuarioId;
            const includes = "includes=boletos,pix,cartoes_credito,cartas_credito,cupons_desconto,pedido,forma_pagamento&"

            const request: IDataRequest = {
                url: this.path + "/?" + includes + filter + "orderby=id",
            }

            const response: IDataResponse = await this.httpClient.get(request);

            return response;
        } catch (error) {
            return {
                data: null,
                message: "Erro ao consultar endereços",
                statusCode: 404
            }
        }
    }

    // Vincula ou cria pagamento com o pedido
    async savePagamentosPedido(codigo_venda: string, termosAceite: ITermosPagamentos, cupomDesconto?: PedidoCupomDesconto,): Promise<IDataResponse> {
        try {
            const user = userInfo$.getValue();

            termosAceite.termos_aceite.sessao = {
                os: navigator.platform,
                userAgent: navigator.userAgent,
                language: navigator.language,
                user: user?.user || "",
                geolocation: null
            }
            navigator.geolocation.getCurrentPosition((geo) => {
                if (geo && termosAceite && termosAceite.termos_aceite.sessao) {
                    termosAceite.termos_aceite.sessao.geolocation = geo
                }
            })


            const request: IDataRequest = {
                url: this.path + "/pedido/" + codigo_venda,
                data: termosAceite
            }


            if (cupomDesconto) {
                request.data = {
                    ...request.data,
                    cupom_desconto: cupomDesconto
                };
            }

            const response: IDataResponse = await this.httpClient.post(request);

            return response;
        } catch (error) {
            return {
                data: null,
                message: "Erro ao atualizar pagamento/pedido",
                statusCode: 404
            }
        }
    }

    // Vincula cliente com pagamento
    async savePagamentosClienteEnderecos(codigo_venda: string, clienteId: number, enderecoId: number): Promise<IDataResponse> {
        try {
            const request: IDataRequest = {
                url: this.path + "/cliente_enderecos/" + codigo_venda,
                data: {
                    cliente: clienteId,
                    entrega: enderecoId
                }
            }

            const response: IDataResponse = await this.httpClient.post(request);

            return response;
        } catch (error) {
            return {
                data: null,
                message: "Erro ao atualizar pagamento/pedido",
                statusCode: 404
            }
        }
    }


    async getParcelas(params: IGetParcelas): Promise<IDataResponse<CartaoCreditoParcelaType[] | null>> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/cartoes_credito/parcelas/${params.codigo_venda}`,
                data: {
                    "bandeira": params.bandeira,
                    "valor": params.valor,
                    "entrada": params.entrada
                }
            }

            const response = await this.httpClient.post(request);

            if (response.statusCode !== STATUS_CODE.OK) {
                return {
                    data: [],
                    statusCode: STATUS_CODE.NO_CONTENT,
                    message: response.message
                }
            }

            const data: CartaoCreditoParcelaType[] = response.data;

            for (let idxParcela in data) {
                data[idxParcela].label = `${data[idxParcela].parcelas}x ${Formater.toString(data[idxParcela].valor, TypeRecords.CURRENCY)}${data[idxParcela].juros ? " (com juros)" : " (sem juros)"}`
            }

            const _parcelas = data;
            return {
                data: _parcelas,
                statusCode: STATUS_CODE.OK,
                message: "Ok"
            }

        } catch (error) {
            return {
                data: null,
                message: "Erro ao obter parcelas",
                statusCode: STATUS_CODE.BAD_REQUEST
            }
        }

    }

    async getFormasPagamento(codigo_venda: string): Promise<FormaPagamentoType[]> {
        try {
            const request: IDataRequest = {
                url: `${this.path}/formas_pagamento/${codigo_venda}?orderby=grupo`,
            }

            const response: IDataResponse = await this.httpClient.get(request);

            if (response.statusCode === STATUS_CODE.OK) {
                return response.data?.row || response.data
            }

            return [];
        } catch (error) {
            return [];
        }
    }

    async confirmarPagamento(codigo_venda: string, pagamento: PedidoPagamento): Promise<IDataResponse> {
        try {
            const request: IDataRequest = {
                url: this.path + "/formas_pagamento/" + codigo_venda,
                data: {
                    forma_pagamento: pagamento.forma_pagamento,
                    pagamentos: pagamento.itens
                }
            }

            const response: IDataResponse = await this.httpClient.post(request);

            return response;
        } catch (error) {
            return {
                data: null,
                message: "Erro ao atualizar pagamento/pedido",
                statusCode: 404
            }
        }

    }


    async verificarStatusServicoCupom(): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/servico_cupom/verificar`
            }

            const response: IDataResponse = await this.httpClient.get(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: "Erro ao verificar o serviço de cupons de desconto",
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao verificar o serviço de cupons de desconto",
                data: null,
            }
        }
    }


    async consultarCupom(cupom: ConsultarCupomDesconto): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/servico_cupom/consultar`,
                data: cupom
            }

            const response: IDataResponse = await this.httpClient.post(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }


            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: "Erro ao consultar cupons de desconto (1)",
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao consultar cupons de desconto (2)",
                data: null,
            }
        }
    }

    async consumoCupom(cupom: string, codigoVenda: string): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/servico_cupom/consumir/` + codigoVenda,
                data: {
                    cupom: cupom,
                }
            }

            const response: IDataResponse = await this.httpClient.post(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }


            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: "Erro ao consultar cupons de desconto (1)",
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao consultar cupons de desconto (2)",
                data: null,
            }
        }
    }

    async efetivaCupom(cupom: CupomDescontoEfetivar): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/servico_cupom/efetivar/` + cupom.codigoVenda,
                data: {
                    cupom: cupom.cupom
                }
            }

            const response: IDataResponse = await this.httpClient.post(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }


            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: "Erro ao efetivar cupons de desconto (1)",
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao efetivar cupons de desconto (2)",
                data: null,
            }
        }
    }

    async validarCupom(cupom: CupomDescontoValidar): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/servico_cupom/validar/` + cupom.codigoVenda,
                data: cupom,
            }

            const response: IDataResponse = await this.httpClient.post(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response;
            }


            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao validar cupons de desconto (2)",
                data: null,
            }
        }
    }

    async excluirCupom(cupom: string, codigoVenda: string): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/servico_cupom/` + codigoVenda,
                data: {
                    cupom: cupom,
                }
            }

            const response: IDataResponse = await this.httpClient.post(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }


            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao excluir cupons de desconto (2)",
                data: null,
            }
        }
    }

    async getBoleto(pagamentoId: number): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/boletos?pagamento_id=${pagamentoId}&situacao=gerado`
            }

            const response: IDataResponse = await this.httpClient.get(request);

            if (response.statusCode === STATUS_CODE.OK) {
                const responseData = {
                    ...response,
                }

                if (response?.data?.length > 0) {
                    responseData.data = response.data[0]
                }

                return responseData;
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: "Erro ao consultar boleto (1)",
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao consultar boleto (2)",
                data: null,
            }
        }
    }

    async simularFinanciamento(codigoVenda: string, simulacao: Simulacao): Promise<IDataResponse<FinanciamentoSimulacao[]>> {
        try {

            if (codigoVenda === "") {
                throw new InvalidException("Códido de venda não informado");
            }

            const request: IDataRequest = {
                url: `${this.path}/financiamentos/simulacoes/${codigoVenda}`,
                data: simulacao
            }

            const response: IDataResponse = await this.httpClient.post(request);

            if (response.statusCode !== STATUS_CODE.OK) {
                return {
                    data: [],
                    statusCode: STATUS_CODE.NO_CONTENT,
                    message: "Erro ao simular financimanto"
                }

            }

            return response;


        } catch (error) {
            return {
                data: [],
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao simular financimanto"
            }
        }
    }

    async parcelasDisponiveis(): Promise<IDataResponse<IParcelasDisponiveis>> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/financiamentos/parcelas`,
            }

            const response: IDataResponse = await this.httpClient.get(request);

            if (response.statusCode !== STATUS_CODE.OK) {
                return {
                    data: { parcelas: [] },
                    statusCode: STATUS_CODE.NO_CONTENT,
                    message: "Erro ao obter configurações de parcelas"
                }

            }

            return response;


        } catch (error) {
            return {
                data: { parcelas: [] },
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao obter configurações de parcelas"
            }
        }
    }


    async gerarBoleto(codigoVenda: string): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/boletos/geracao/meclado/` + codigoVenda,
            }

            const response: IDataResponse = await this.httpClient.put(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao excluir cupons de desconto (2)",
                data: null,
            }
        }
    }

    async obtemClientePedido(codigoVenda: string): Promise<IDataResponse<Clientes | null>> {
        try {
            const request: IDataRequest = {
                url: `${this.path}/cliente_pedido/` + codigoVenda,
            }

            const response: IDataResponse<Clientes | null> = await this.httpClient.get(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao obter dados do cliente da proposta",
                data: null,
            }
        }
    }

    async obtemGruposAtividades(financeira_id: number): Promise<IDataResponse> {
        try {
            const request: IDataRequest = {
                url: `${this.path}/financiamentos/empresas/grupos_atividades/` + financeira_id,
            }

            const response: IDataResponse = await this.httpClient.get(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao obter grupos de atividades",
                data: null,
            }
        }
    }

    async obtemRelacionamentos(financeira_id: number): Promise<IDataResponse> {
        try {
            const request: IDataRequest = {
                url: `${this.path}/financiamentos/empresas/relacionamentos/` + financeira_id,
            }

            const response: IDataResponse = await this.httpClient.get(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao obter relacionamentos",
                data: null,
            }
        }
    }

    async obtemEmpresaAtividades(financeira_id: number): Promise<IDataResponse> {
        try {
            const request: IDataRequest = {
                url: `${this.path}/financiamentos/empresas/atividades/` + financeira_id,
            }

            const response: IDataResponse = await this.httpClient.get(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao obter lista de atividades de empresa",
                data: null,
            }
        }
    }

    async obtemEmpresaTipos(financeira_id: number): Promise<IDataResponse> {
        try {
            const request: IDataRequest = {
                url: `${this.path}/financiamentos/empresas/tipos/` + financeira_id,
            }

            const response: IDataResponse = await this.httpClient.get(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao obter tipos de atividades de empresa",
                data: null,
            }
        }
    }

    async obtemNaturezasLegais(financeira_id: number): Promise<IDataResponse> {
        try {
            const request: IDataRequest = {
                url: `${this.path}/financiamentos/empresas/naturezas_legais/` + financeira_id,
            }

            const response: IDataResponse = await this.httpClient.get(request)

            if (response.statusCode === STATUS_CODE.OK) {
                return response
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: response.message,
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao obter tipos de atividades de empresa",
                data: null,
            }
        }
    }


    async getPix(codigo_venda: string): Promise<IDataResponse> {
        try {

            const request: IDataRequest = {
                url: `${this.path}/pix/gerar/${codigo_venda}`
            }

            const response: IDataResponse = await this.httpClient.post(request);

            if (response.statusCode === STATUS_CODE.OK) {
                const responseData = {
                    ...response,
                }

                if (response?.data?.length > 0) {
                    responseData.data = response.data[0]
                }

                return responseData;
            }

            return {
                statusCode: STATUS_CODE.NO_CONTENT,
                message: "Erro ao consultar pix (1)",
                data: null,
            }
        } catch (error) {
            return {
                statusCode: STATUS_CODE.BAD_REQUEST,
                message: "Erro ao consultar pix (2)",
                data: null,
            }
        }
    }
}

const pagamentoProvider = new PagamentosProvider();
export default pagamentoProvider;
export { PagamentosProvider }